import {
  DomainProbeResult,
  DomainProbeStatus,
} from '@tribeplatform/gql-client/types'
import { T } from '@tribeplatform/react-components/i18n'
import { Link } from '@tribeplatform/react-components/Link'
import { HelperText } from '@tribeplatform/react-ui-kit/HelperText'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { Text } from '@tribeplatform/react-ui-kit/Text'

import { RuntimeConfigs } from '../../../../../../../configs/runtime.js'

interface Props {
  probeResult: DomainProbeResult
  canUseCustomDomain: boolean
}
export const DomainInputHelper = ({
  probeResult,
  canUseCustomDomain,
}: Props) => {
  const probeStatus = probeResult?.status
  switch (probeStatus) {
    case DomainProbeStatus.Invalid:
      return (
        <HelperText status="error" highlighted className="mt-2">
          <T
            defaultMessage="Please enter a valid domain or subdomain. The domain should be owned by you and addressable under <psl-link>Public Suffix List</psl-link>."
            id="Admin.Domain.Error.InvalidHelperText"
            values={{
              'psl-link': chunks => (
                <Link
                  variant="accent"
                  href="https://publicsuffix.org/"
                  target="_blank"
                >
                  {chunks}
                </Link>
              ),
            }}
          />
        </HelperText>
      )

    case DomainProbeStatus.Active:
      return (
        <HelperText status="error" highlighted className="mt-2">
          <T
            defaultMessage="Your site is already using this domain. Please choose a different address."
            id="Admin.Domain.Error.ActiveHelperText"
          />
        </HelperText>
      )

    case DomainProbeStatus.Taken:
      return (
        <HelperText status="error" highlighted className="mt-2">
          <T
            defaultMessage="This domain is assigned to a different site. Please choose a different address."
            id="Admin.Domain.Error.TakenHelperText"
          />
        </HelperText>
      )

    case DomainProbeStatus.Reserved: {
      const { subdomain, apex, domain } = probeResult.parseResult.parts
      const isUsingCorrectNativeDomain = domain.endsWith(
        RuntimeConfigs.VALID_NATIVE_DOMAIN,
      )
      const isMultiLevelSubdomain = subdomain.includes('.')
      if (isUsingCorrectNativeDomain && isMultiLevelSubdomain) {
        const alternateDomain = `${subdomain.replace('.', '-')}.${apex}`
        return (
          <HelperText status="error" highlighted className="mt-2">
            <T
              defaultMessage="You cannot use multi-level subdomain under the {native_domain} domain.<br></br>You may want to try {alternate_domain} instead."
              id="Admin.Domain.Error.Invalid.MultiLevelSubdomainHelperText"
              values={{
                alternate_domain: (
                  <strong className="font-mono">{alternateDomain}</strong>
                ),
                native_domain: (
                  <strong className="font-mono">
                    {RuntimeConfigs.VALID_NATIVE_DOMAIN}
                  </strong>
                ),
                br: () => <br />,
              }}
            />
          </HelperText>
        )
      }
      return (
        <HelperText status="error" highlighted className="mt-2">
          <T
            defaultMessage="This address is reserved by Bettermode."
            id="Admin.Domain.Error.Reserved.HelperText"
          />
        </HelperText>
      )
    }

    case DomainProbeStatus.Pending:
    case DomainProbeStatus.ReadyForTransfer:
    case DomainProbeStatus.RestrictedFeature:
      // These states are not valid for this component
      return null

    case DomainProbeStatus.Available: {
      const {
        parts: { domain: newDomain },
        isApex,
      } = probeResult.parseResult
      if (!isApex) {
        return (
          <HelperText status="info" highlighted className="mt-2">
            <T
              defaultMessage="{new_domain} is not taken by another site on Bettermode. You can start the migration using the button below. Your site will remain accessible at its current address during the migration and we will ask for a final confirmation before making the switch."
              id="Admin.Domain.Available.HelperText"
              values={{
                new_domain: (
                  <strong className="font-mono inline-block">
                    <pre>{newDomain}</pre>
                  </strong>
                ),
              }}
            />
          </HelperText>
        )
      }
      return (
        <HelperText status="neutral" highlighted className="mt-2">
          <T
            defaultMessage="We recommend using the {www_sub} version of your domain, instead of the root domain. Not every DNS provider supports root domain setup and you may have to change your DNS provider in order to complete migration. <link>Learn more</link>"
            id="Admin.Domain.Available.ApexHelperText"
            values={{
              new_domain: newDomain,
              // eslint-disable-next-line react/jsx-no-literals
              www_sub: <strong className="font-mono">www</strong>,
              link: chunks => (
                <Link
                  variant="accent"
                  href="http://bettermode.com/hub/branding/post/use-a-custom-domain-or-subdomain-qfTp9h6Y30WEX6F"
                >
                  <span className="inline-block">
                    {chunks}
                    <SvgIcon name="link-external" className="ms-1" inline />
                  </span>
                </Link>
              ),
            }}
          />
        </HelperText>
      )
    }

    default: {
      const _exhaustiveCheck: never = probeStatus
      if (canUseCustomDomain) {
        return (
          <Text>
            <T
              defaultMessage="Enter the domain or subdomain that you want to move your site to. You should already own the domain and have access to its DNS settings in order to complete the migration. We will guide you through the process. <link>Learn more</link>"
              id="Admin.Domain.Input.HelperText"
              values={{
                link: chunks => (
                  <Link
                    variant="accent"
                    href="http://bettermode.com/hub/branding/post/use-a-custom-domain-or-subdomain-qfTp9h6Y30WEX6F"
                  >
                    <span className="inline-block">
                      {chunks}
                      <SvgIcon name="link-external" className="ms-1" inline />
                    </span>
                  </Link>
                ),
              }}
            />
          </Text>
        )
      }

      return (
        <HelperText
          status="info"
          highlighted
          className="text-content-on-highlight"
        >
          <T
            defaultMessage="You can freely choose any subdomain under {native_domain}, as long as it’s not taken by another site. However, to use a custom domain, you need to upgrade your plan. <link>View available plans</link>."
            id="Admin.DomainSettings.New.Upgrade"
            values={{
              link: chunks => (
                <Link variant="accent" href="/manage/billing/plans">
                  {chunks}
                </Link>
              ),
              native_domain: (
                <strong className="font-mono">
                  {RuntimeConfigs.VALID_NATIVE_DOMAIN}
                </strong>
              ),
            }}
          />
        </HelperText>
      )
    }
  }
}
